import { useState, useEffect, useCallback } from 'react';
import { motion, Reorder } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  ChevronRight, Plus, Edit2, Trash2, Loader2, FolderTree,
  Layers, GripVertical, ToggleLeft, ToggleRight, ArrowLeft, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parentCategories = [
  { value: 'onglerie', label: 'Onglerie', color: 'bg-rose-100 text-rose-700' },
  { value: 'pedicure', label: 'Pédicure', color: 'bg-pink-100 text-pink-700' },
  { value: 'bien_etre', label: 'Bien-être', color: 'bg-purple-100 text-purple-700' }
];

const emptyForm = {
  name: '',
  parent_category: 'onglerie',
  description: '',
  is_active: true
};

export default function AdminSubcategoriesPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!token) return;
    fetchSubcategories();
  }, [token]);

  const fetchSubcategories = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/subcategories?include_inactive=true`, { headers });
      setSubcategories(response.data.subcategories || []);
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      toast.error('Erreur lors du chargement des sous-catégories');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingSubcategory(null);
    setFormData(emptyForm);
    setIsDialogOpen(true);
  };

  const openEditDialog = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      parent_category: subcategory.parent_category,
      description: subcategory.description || '',
      is_active: subcategory.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setIsSaving(true);
    try {
      if (editingSubcategory) {
        await axios.put(`${API_URL}/admin/subcategories/${editingSubcategory.id}`, formData, { headers });
        toast.success('Sous-catégorie modifiée');
      } else {
        await axios.post(`${API_URL}/admin/subcategories`, formData, { headers });
        toast.success('Sous-catégorie créée');
      }
      setIsDialogOpen(false);
      fetchSubcategories();
    } catch (err) {
      console.error('Error saving subcategory:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (subcategory) => {
    if (!window.confirm(`Supprimer la sous-catégorie "${subcategory.name}" ?`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/subcategories/${subcategory.id}`, { headers });
      toast.success('Sous-catégorie supprimée');
      fetchSubcategories();
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (subcategory) => {
    try {
      await axios.patch(`${API_URL}/admin/subcategories/${subcategory.id}/toggle`, {}, { headers });
      toast.success(subcategory.is_active ? 'Sous-catégorie désactivée' : 'Sous-catégorie activée');
      fetchSubcategories();
    } catch (err) {
      console.error('Error toggling subcategory:', err);
      toast.error('Erreur lors de la modification');
    }
  };

  // Move subcategory up/down within its parent category - now handled via Reorder component
  const handleReorder = useCallback((parentCategory, newOrder) => {
    setSubcategories(prev => {
      // Keep items from other parent categories
      const otherCategories = prev.filter(s => s.parent_category !== parentCategory);
      // Update display_order for reordered items
      const reorderedItems = newOrder.map((item, idx) => ({
        ...item,
        display_order: idx
      }));
      return [...otherCategories, ...reorderedItems];
    });
    setHasOrderChanges(true);
  }, []);

  // Save new order
  const saveOrder = async (parentCategory) => {
    const parentSubcats = subcategories
      .filter(s => s.parent_category === parentCategory)
      .sort((a, b) => a.display_order - b.display_order);
    
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/admin/subcategories/reorder`, {
        subcategory_ids: parentSubcats.map(s => s.id)
      }, { headers });
      toast.success('Ordre enregistré');
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  // Group subcategories by parent and sort by display_order
  const groupedSubcategories = parentCategories.map(parent => ({
    ...parent,
    items: subcategories
      .filter(s => s.parent_category === parent.value)
      .sort((a, b) => a.display_order - b.display_order)
  }));

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-12 px-6 bg-[var(--charcoal)]">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Sous-catégories
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Gérez les sous-catégories pour organiser vos prestations
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Sous-catégories</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1000px] mx-auto">
          {/* Header Actions */}
          <div className="flex justify-between items-center mb-8">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              <ArrowLeft size={16} />
              Retour
            </Button>
            <Button
              onClick={openCreateDialog}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
            >
              <Plus size={16} />
              Nouvelle sous-catégorie
            </Button>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
            </div>
          ) : (
            /* Grouped Subcategories */
            <div className="space-y-8">
              {groupedSubcategories.map((group) => (
                <FadeIn key={group.value}>
                  <div className="bg-white border border-[var(--champagne)]">
                    <div className={`px-6 py-4 flex items-center justify-between ${group.color}`}>
                      <div className="flex items-center gap-3">
                        <FolderTree size={20} />
                        <h2 className="font-medium">{group.label}</h2>
                        <span className="text-sm opacity-70">
                          ({group.items.length} sous-catégorie{group.items.length > 1 ? 's' : ''})
                        </span>
                      </div>
                      {group.items.length > 1 && hasOrderChanges && (
                        <Button
                          size="sm"
                          onClick={() => saveOrder(group.value)}
                          disabled={isSaving}
                          className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-1 h-8 text-xs"
                        >
                          {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          Enregistrer l'ordre
                        </Button>
                      )}
                    </div>

                    {group.items.length === 0 ? (
                      <div className="p-8 text-center text-[var(--charcoal)]/50">
                        Aucune sous-catégorie pour {group.label}
                      </div>
                    ) : (
                      <Reorder.Group
                        axis="y"
                        values={group.items}
                        onReorder={(newOrder) => handleReorder(group.value, newOrder)}
                        className="divide-y divide-[var(--champagne)]"
                      >
                        {group.items.map((subcategory, index) => (
                          <Reorder.Item
                            key={subcategory.id}
                            value={subcategory}
                            className={`p-4 flex items-center justify-between bg-white cursor-grab active:cursor-grabbing ${!subcategory.is_active ? 'opacity-50' : ''}`}
                            whileDrag={{
                              scale: 1.02,
                              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                              zIndex: 50
                            }}
                          >
                            <div className="flex items-center gap-4">
                              {/* Drag Handle */}
                              <div className="text-[var(--charcoal)]/30 hover:text-[var(--charcoal)]/60 transition-colors">
                                <GripVertical size={18} />
                              </div>
                              <span className="text-xs text-[var(--charcoal)]/40 w-6 text-center">
                                #{index + 1}
                              </span>
                              <div>
                                <p className="font-medium text-[var(--charcoal)]">
                                  {subcategory.name}
                                </p>
                                {subcategory.description && (
                                  <p className="text-sm text-[var(--charcoal)]/60">
                                    {subcategory.description}
                                  </p>
                                )}
                                <p className="text-xs text-[var(--charcoal)]/40 mt-1">
                                  {subcategory.services_count} prestation{subcategory.services_count > 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleActive(subcategory)}
                                title={subcategory.is_active ? 'Désactiver' : 'Activer'}
                              >
                                {subcategory.is_active ? (
                                  <ToggleRight size={18} className="text-green-500" />
                                ) : (
                                  <ToggleLeft size={18} className="text-[var(--charcoal)]/30" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(subcategory)}
                              >
                                <Edit2 size={16} className="text-[var(--charcoal)]/60" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(subcategory)}
                              >
                                <Trash2 size={16} className="text-red-400" />
                              </Button>
                            </div>
                          </Reorder.Item>
                        ))}
                      </Reorder.Group>
                    )}
                  </div>
                </FadeIn>
              ))}
            </div>
          )}

          {/* Info Box */}
          <FadeIn delay={0.2}>
            <div className="mt-8 p-6 bg-[var(--champagne)]/30 border border-[var(--champagne)]">
              <h3 className="font-medium text-[var(--charcoal)] mb-2 flex items-center gap-2">
                <Layers size={16} className="text-[var(--gold)]" />
                À propos des sous-catégories
              </h3>
              <ul className="text-sm text-[var(--charcoal)]/70 space-y-1">
                <li>• Les sous-catégories permettent d'organiser les prestations au sein d'une catégorie</li>
                <li>• Exemple : Onglerie → Gainage, Gel X, Semi-permanent</li>
                <li>• Les prestations sont triées par catégorie, puis par sous-catégorie sur le site</li>
                <li>• <strong>Ordre d'affichage :</strong> Glissez-déposez les éléments pour réorganiser, puis cliquez "Enregistrer l'ordre"</li>
                <li>• Désactiver une sous-catégorie ne masque pas les prestations associées</li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              {editingSubcategory ? 'Modifier la sous-catégorie' : 'Nouvelle sous-catégorie'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nom <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="ex: Gainage"
              />
            </div>

            <div>
              <Label>Catégorie parente <span className="text-red-500">*</span></Label>
              <Select
                value={formData.parent_category}
                onValueChange={(val) => setFormData(prev => ({ ...prev, parent_category: val }))}
              >
                <SelectTrigger className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {parentCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description (optionnelle)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de la sous-catégorie..."
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="rounded-none"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {editingSubcategory ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
